import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useSwipeToClose from '../../../hooks/useSwipeToClose';
import Spinner from '../../UI/Spinner';

const GroceryBudgetDrawer = ({ 
  isOpen, 
  numberOfPeople, 
  peopleOptions, 
  handlePeopleSelect, 
  budget, 
  budgetOptions, 
  handleBudgetUpdate, 
  closeDrawer, 
  onSave,
  isSaving = false,
  country,
  getBudgetOptionsForPeople
}) => {
  const { t } = useTranslation('wizard');
  const { containerRef, dragOffset, isAnimating, shouldRender, isVisible, isDragging, DragHandle } = useSwipeToClose(closeDrawer, isOpen);

  // Temporary local state for draft changes
  const [tempBudget, setTempBudget] = useState(budget);
  const [tempNumberOfPeople, setTempNumberOfPeople] = useState(numberOfPeople);
  const [tempBudgetOptions, setTempBudgetOptions] = useState(budgetOptions);

  // Reset temp state when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTempBudget(budget);
      setTempNumberOfPeople(numberOfPeople);
      setTempBudgetOptions(budgetOptions);
    }
  }, [isOpen, budget, numberOfPeople, budgetOptions]);

  if (!shouldRender) return null;

  // Handle budget selection (local only)
  const handleTempBudgetUpdate = (option) => {
    setTempBudget(option);
  };

  // Handle people selection (local only) - also recalculate budget options
  const handleTempPeopleSelect = (value) => {
    setTempNumberOfPeople(value);
    
    // Recalculate budget options for the new number of people
    if (getBudgetOptionsForPeople) {
      const newBudgetOptions = getBudgetOptionsForPeople(value);
      setTempBudgetOptions(newBudgetOptions);
      
      // Update temp budget to match the same category with new amounts
      if (tempBudget && tempBudget.key) {
        const updatedBudget = newBudgetOptions.find(opt => opt.key === tempBudget.key);
        if (updatedBudget) {
          setTempBudget(updatedBudget);
        }
      }
    }
  };

  // Save changes to parent and close
  const handleSave = () => {
    // Apply changes to parent
    if (tempNumberOfPeople !== numberOfPeople) {
      handlePeopleSelect(tempNumberOfPeople);
    }
    if (tempBudget?.key !== budget?.key) {
      handleBudgetUpdate(tempBudget);
    }
    onSave();
  };

  return createPortal(
    <div 
      className={`fixed flex items-end justify-center inset-0 bg-[#0D0C07] z-[200] drawer-overlay`} 
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: 'background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
      onClick={closeDrawer}
    >
      <div 
        ref={containerRef}
        className={`flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content max-h-[90vh]`}
        style={{
          transform: isVisible 
            ? `translateY(${Math.max(0, dragOffset)}px)` 
            : 'translateY(100%)',
          transition: (dragOffset === 0 && isAnimating) || (!isDragging && dragOffset === 0)
            ? 'transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
            : 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle */}
        <DragHandle />
        {/* Close button */}
        <button 
          onClick={closeDrawer}
          className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-20"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D"/>
          </svg>
        </button>
        
        {/* Scrollable content including header */}
        <div className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden p-8 px-6 pb-[116px]">
          
          {/* Number of People Section - Moved above Budget */}
          <h2 className="
            text-[var(--Color-Grays-Gray-10,#0D0C07)]
            text-[16px]
            font-bold
            leading-[32px]
            w-full
            mb-3
          ">{t('people.title')}</h2>
          <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-3 mb-8">
            {peopleOptions.map((num) => (
              <button
                key={num}
                className={`p-3 text-left text-[14px] leading-[20px] font-semibold rounded-[12px] border-[0.5px] border-[#0D0C07] ${
                  tempNumberOfPeople === num 
                    ? 'bg-[#FFCB00] md:hover:bg-[#FFCB00]/90' 
                    : 'bg-white md:hover:bg-[#F8F8F7]'
                }`}
                onClick={() => handleTempPeopleSelect(num)}
              >
                {num}
              </button>
            ))}
          </div>

          {/* Budget Section */}
          <h2 className="
            text-[var(--Color-Grays-Gray-10,#0D0C07)]
            text-[16px]
            font-bold
            leading-[32px]
            w-full
          ">{t('budget.title')}</h2>
          <p className="
            text-[var(--Color-Grays-Gray-10,#65635D)]
            text-[14px]
            font-normal
            leading-[24px]
            mb-3
          ">{t('budget.subtitle')}</p>
          <div className="grid grid-cols-3 gap-3 mb-2">
            {tempBudgetOptions.map((option) => {
              // Get the category name from the key (capitalize first letter)
              const category = t(`budget.${option.key}`);
              // Get max value and currency symbol
              const maxAmount = option.max;
              const currencySymbol = option.currency || '$';
              
              return (
                <button
                  key={option.key}
                  className={`p-3 text-center rounded-[12px] border-[0.5px] border-[#0D0C07] transition-colors ${
                    tempBudget?.key === option.key
                      ? 'bg-[#FFCB00] md:hover:bg-[#FFCB00]/90' 
                      : 'bg-white md:hover:bg-[#F8F8F7]'
                  }`}
                  onClick={() => handleTempBudgetUpdate(option)}
                >
                  {/* Mobile: stacked vertical, Desktop: inline horizontal with bullet */}
                  <div className="flex flex-col md:flex-row md:justify-center md:gap-0 items-center">
                    <span className="text-[14px] leading-[20px] font-semibold text-[#0D0C07]">
                      {category}
                    </span>
                    {maxAmount && (
                      <>
                        <span className="hidden md:inline text-[14px] leading-[20px] font-normal text-[rgba(13,12,7,0.7)] mx-1">•</span>
                        <span className="md:text-[14px] text-[12.4px] md:pt-0 pt-0.5 leading-[20px] font-normal text-[rgba(13,12,7,0.7)]">
                          {'<'}{maxAmount}{currencySymbol}
                        </span>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Budget helper text */}
          <p className="
            text-[13px]
            font-normal
            leading-[20px]
            text-[#65635D]
            mt-2
          ">{t('budget.helperText', { count: tempNumberOfPeople, people: tempNumberOfPeople === 1 ? t('people.person') : t('people.people') })}</p>

        </div>
        
        {/* Fixed Save Button */}
        <div
          className="
            fixed bottom-[16px] left-[20px] right-[20px]
            py-2 px-2 
            flex justify-center
            bg-[linear-gradient(135deg,_rgba(255,255,255,0.6)_0%,_rgba(255,255,255,0.8)_100%)]
            backdrop-blur-lg
            z-50
            max-w-5xl
            mx-auto
            rounded-[96px]
            border-[0.5px] border-[linear-gradient(145deg,_rgba(255,255,255,0.8)_0%,_rgba(255,255,255,1)_100%)]
            shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
          " 
        >
          <div className="w-full max-w-5xl">
            <button
              className={`
                inline-flex justify-center items-center gap-1
                h-[48px] px-[24px] py-[14px]
                rounded-[32px]
                border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                bg-[#090]
                text-[#FFF] text-center
                text-[14px] font-medium leading-[28px]
                transition-colors
                w-full
                shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
                ${
                  isSaving
                    ? "cursor-not-allowed opacity-60 bg-[#090]"
                    : "cursor-pointer bg-[linear-gradient(200deg,_#090_0%,_#060_100%)] md:hover:border-[0.5px] md:hover:border-[#060] md:hover:bg-[#060]"
                }
              `}
              onClick={isSaving ? undefined : handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Spinner size="sm" color="#FFF" />
                  <span>{t('common:buttons.saving', 'Saving...')}</span>
                </>
              ) : (
                <span>{t('common:buttons.save', 'Save')}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>, document.body
  );
};

export default GroceryBudgetDrawer;
